import { Component, h, Host, State } from '@stencil/core';
import { get } from '~service/ajax-service';
import { translate } from '~service/translation-service';

interface Filters {
  years: number[];
  months: string[];
  yearsMonths: string[];
}

interface Years {
  [key: number]: string;
}

interface Event {
  uid: number;
  title: string;
  teaser: string;
  image: string;
  location: string;
  formattedDate: string;
  year: number;
  month: string;
  link: {
    self: string;
  };
}

@Component({
  tag: 'uvex-event-list',
  styleUrl: './event-list.scss',
})
export class eventList {
  currentYear = new Date().getFullYear();
  years: Years = {};

  filters: Filters = {
    years: [],
    months: [],
    yearsMonths: [],
  };

  @State() activeFilters = {
    year: new Date().getFullYear(),
    month: '',
  };

  @State() events: Event[] = [];
  @State() filteredEvents: Event[] = [];

  async componentWillLoad() {
    const { events, years, months } = await get('/current-events');
    this.years = years;
    this.filters.years = Object.keys(years).map(a => parseInt(a));
    this.filters.months = months;
    this.processResponse(events);
  }

  async changeYear(year: number) {
    this.activeFilters = { ...this.activeFilters, ...{ year: year } };
    const events = (await get(this.years[year])) as Event[];
    this.processResponse(events);
  }

  changeMonth(month: string) {
    if (!this.hasResultForMonth(month)) {
      return;
    }
    this.activeFilters = { ...this.activeFilters, ...{ month: month } };
    this.filterEvents();
  }

  hasResultForMonth(month: string): boolean {
    return this.filters.yearsMonths.indexOf(`${this.activeFilters.year}-${month}`.toLowerCase()) !== -1;
  }

  filterEvents() {
    const { year, month } = this.activeFilters;
    this.filteredEvents = this.events.filter(event => `${year}-${month}`.toLowerCase() === `${event.year}-${event.month}`.toLowerCase());
  }

  private processResponse(events: Event[]) {
    this.events = events;
    this.filters.yearsMonths = events
      .map(event => `${event.year}-${event.month}`.toLowerCase())
      .filter((yearMonth, index, yearsMonths) => yearsMonths.indexOf(yearMonth) === index);

    this.setBestMatchingMonthFilterForCurrentYear();
    this.filterEvents();
  }

  private getMonthName(date: Date): string {
    return this.filters.months[date.getMonth()];
  }

  /**
   * Setting the month filter needs to be delayed until
   * events are in place. The best matching month is
   * the earliest month in case we're displaying future
   * events or the latest in case we're displaying past.
   */
  private setBestMatchingMonthFilterForCurrentYear() {
    let monthName = '',
      monthNumber = 0,
      month = new Date('2012-01-01');

    const { year } = this.activeFilters;

    const trySetting = monthNumber => {
      month.setMonth(monthNumber);
      monthName = this.getMonthName(month);
      if (this.hasResultForMonth(monthName)) {
        this.changeMonth(monthName);
        return true;
      }
      return false;
    };

    if (year > new Date().getFullYear()) {
      for (monthNumber = 0; monthNumber < 12; monthNumber++) {
        if (trySetting(monthNumber)) {
          return;
        }
      }
    } else if (year === new Date().getFullYear()) {
      for (monthNumber = new Date().getMonth(); monthNumber < 12; monthNumber++) {
        if (trySetting(monthNumber)) {
          return;
        }
      }
      for (monthNumber = new Date().getMonth(); monthNumber >= 0; monthNumber--) {
        if (trySetting(monthNumber)) {
          return;
        }
      }
    } else {
      for (monthNumber = 11; monthNumber >= 0; monthNumber--) {
        if (trySetting(monthNumber)) {
          return;
        }
      }
    }
  }

  private renderFacets() {
    const yearOptions = Object.keys(this.years).map(year => (
      <option value={year} selected={this.activeFilters.year === parseInt(year, 10)}>
        {year}
      </option>
    ));

    return (
      <div class="search-facet clearfix">
        <div class="row">
          <div class="col-xs-12 col-md-4">
            <label htmlFor="filter-year">{translate('filter.year')}</label>
            <select id="filter-year" class="form-control" onChange={event => this.changeYear(parseInt((event.target as HTMLSelectElement).value, 10))}>
              {yearOptions}
            </select>
          </div>
          <div class="col-xs-12 col-md-8">
            <label>{translate('filter.month')}</label>
            <div class="radio-group">
              {this.filters.months.map(month => (
                <div class="radio">
                  <input type="radio" name="filter" value={month} checked={month === this.activeFilters.month} disabled={!this.hasResultForMonth(month)} />
                  <label onClick={_ => this.changeMonth(month)}>{month.substring(0, 3)}</label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderEvents() {
    return <div class="search-results-items">{this.filteredEvents.map(event => this.renderEvent(event))}</div>;
  }

  private renderEvent(event) {
    let image = <div class="search-result-image" />;
    if (event.image) {
      image = <div class="search-result-image" style={{ backgroundImage: `url(${event.image})` }} />;
    }

    return (
      <div class="search-result-item clearfix">
        {image}
        <div class="search-result-content">
          <h3>
            <a class="link-full" href={event.link.self}>
              {event.title}
            </a>
          </h3>
          <p>
            <strong>
              {event.formattedDate}
              {event.location && (
                <span>
                  &nbsp;{translate('in')} {event.location}
                </span>
              )}
            </strong>
            <br />
            {event.teaser}
            <a class="search-result-more nowrap" href={event.link.self}>
              {translate('more')}
            </a>
          </p>
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host>
        {this.renderFacets()}
        <h3 class="event-headline">
          <span>{this.activeFilters.month}</span> {this.activeFilters.year}
        </h3>
        {this.renderEvents()}
      </Host>
    );
  }
}
